import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Wand2, Trophy, Film, Eye, Pencil } from "lucide-react";
import { Shell, Stat, Modal } from "../components/Shell";
import { BlocksEditor } from "../components/BlocksEditor";
import { startPreview } from "../components/PreviewBanner";
import { useLang } from "../i18n";
import { api, homeFor } from "../api";
import { ASSETS } from "../assets";

const GRADES = [3, 4, 5, 6, 7, 8, 9];
const BOOKS = { 3: "Jet 1", 4: "Jet 2", 5: "Fantastic Five", 6: "Think About It", 7: "Sky High", 8: "Behind the Scenes", 9: "English Lab 9" };

export default function Owner() {
  const { t } = useLang();
  const [tab, setTab] = useState("cms");
  const [cms, setCms] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editTab, setEditTab] = useState("fields");
  const [gradeFilter, setGradeFilter] = useState("");
  const [stats, setStats] = useState({});
  const [schools, setSchools] = useState([]);
  const [users, setUsers] = useState([]);
  const [topics, setTopics] = useState([]);
  const [comps, setComps] = useState([]);
  const [videos, setVideos] = useState([]);
  const [leads, setLeads] = useState([]);
  const [lbScope, setLbScope] = useState("grade");
  const [video, setVideo] = useState({});
  const [ai, setAi] = useState({});
  const [editUser, setEditUser] = useState(null);
  const [budget, setBudget] = useState({});

  const previewAs = async (u) => {
    try {
      const { data } = await api.post(`/owner/impersonate/${u.id}`);
      localStorage.setItem("el_preview_label", `${u.full_name} (${t(u.role)})`);
      startPreview(data.token);
      window.location.href = homeFor(data.user.role);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّرت المعاينة"); }
  };

  const saveUserEdit = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const body = {
      full_name: f.get("full_name"), username: f.get("username"), role: f.get("role"),
    };
    if (f.get("school_id")) body.school_id = f.get("school_id");
    if (f.get("grade")) body.grade = Number(f.get("grade"));
    if (f.get("password")) body.password = f.get("password");
    try {
      await api.put(`/users/${editUser.id}`, body);
      toast.success("تم تحديث الحساب"); setModal(null); load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "failed"); }
  };
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, st, u, tp, c, v] = await Promise.all([
      api.get("/schools"), api.get("/owner/stats"), api.get("/users"),
      api.get("/topics"), api.get("/competitions"), api.get("/videos"),
    ]);
    setSchools(s.data); setStats(st.data); setUsers(u.data);
    setTopics(tp.data); setComps(c.data); setVideos(v.data);
    api.get("/cms/overview").then((r) => setCms(r.data)).catch(() => {});
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
    api.get("/settings/leaderboard").then((r) => setLbScope(r.data.scope)).catch(() => {});
    api.get("/settings/video").then((r) => setVideo(r.data)).catch(() => {});
    api.get("/settings/ai-limits").then((r) => setAi(r.data)).catch(() => {});
    api.get("/settings/ai-budget").then((r) => setBudget(r.data)).catch(() => {});
  };
  useEffect(() => { load().catch(() => toast.error("Load failed")); }, []);

  const nav = [
    { key: "dashboard", to: "/owner", label: t("dashboard"), icon: ASSETS.progress },
  ];

  const tabs = [["cms", "لوحة الإدارة"], ["schools", t("schools")], ["users", t("users")],
    ["topics", t("topicsMgmt")], ["videos", t("videos")], ["comps", t("competitions")],
    ["leads", "طلبات التجربة"]];

  const setLeadStatus = async (id, status) => {
    await api.patch(`/leads/${id}`, { status });
    toast.success("تم تحديث الحالة");
    api.get("/leads").then((r) => setLeads(r.data));
  };

  const deleteLead = async (id) => {
    await api.delete(`/leads/${id}`);
    setLeads((ls) => ls.filter((l) => l.id !== id));
  };

  const addSchool = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    await api.post("/schools", { name: f.get("name"), city: f.get("city") });
    setModal(null); toast.success("تم إنشاء المدرسة"); load();
  };

  const addUser = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.post("/users", {
        username: f.get("username"), password: f.get("password"), full_name: f.get("full_name"),
        role: f.get("role"), school_id: f.get("school_id"),
        grade: f.get("grade") ? Number(f.get("grade")) : null,
      });
      setModal(null); toast.success("تم إنشاء الحساب"); load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "failed"); }
  };

  const importCsv = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const file = f.get("file");
    const schoolId = f.get("school_id");
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/users/bulk-import?school_id=${schoolId}`, fd);
      toast.success(`تم إنشاء ${data.created} حساب`);
      setModal(null); load();
    } catch { toast.error("import failed"); } finally { setBusy(false); }
  };

  const saveTopic = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const payload = {
      grade: Number(f.get("grade")), title_en: f.get("title_en"), title_ar: f.get("title_ar"),
      summary_ar: f.get("summary_ar"), content_en: f.get("content_en"),
      words: (f.get("words") || "").split(",").map((w) => w.trim()).filter(Boolean),
      video_url: f.get("video_url"), video_id: f.get("video_id") || null,
      reference_book: editing.reference_book || BOOKS[Number(f.get("grade"))],
    };
    await api.put(`/topics/${editing.id}`, payload);
    setEditing(null); toast.success("تم تحديث الدرس"); load();
  };

  const addTopic = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const grade = Number(f.get("grade"));
    await api.post("/topics", {
      grade, title_en: f.get("title_en"), title_ar: f.get("title_ar"),
      summary_ar: f.get("summary_ar"), content_en: f.get("content_en"),
      words: (f.get("words") || "").split(",").map((w) => w.trim()).filter(Boolean),
      video_url: f.get("video_url"), video_id: f.get("video_id") || null, reference_book: BOOKS[grade],
    });
    setModal(null); toast.success("تم إضافة الموضوع"); load();
  };

  const uploadVideo = async (e) => {
    e.preventDefault();
    const file = new FormData(e.target).get("file");
    if (!file || !file.size) return toast.error("اختر ملف فيديو");
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/videos", fd, { timeout: 600000 });
      toast.success("تم رفع الفيديو"); setModal(null); load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "فشل الرفع");
    } finally { setBusy(false); }
  };

  const attachVideo = async (topic, videoId) => {
    await api.put(`/topics/${topic.id}`, { ...topic, video_id: videoId || null });
    toast.success("تم ربط الفيديو بالموضوع"); load();
  };

  const aiFill = async (form) => {
    const title = form.title_en.value, grade = Number(form.grade.value);
    if (!title) return toast.error("اكتب عنوان الموضوع أولاً");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/topic-content", { title, grade });
      form.summary_ar.value = data.summary_ar || "";
      form.content_en.value = data.content_en || "";
      form.words.value = (data.words || []).join(", ");
      toast.success("تم التوليد بالذكاء الاصطناعي");
    } catch { toast.error("AI failed"); } finally { setBusy(false); }
  };

  const addComp = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    setBusy(true);
    try {
      await api.post("/competitions", {
        title: f.get("title"), grade: Number(f.get("grade")), topic: f.get("topic"),
        starts_at: f.get("starts_at") ? new Date(f.get("starts_at")).toISOString() : null,
        ends_at: f.get("ends_at") ? new Date(f.get("ends_at")).toISOString() : null,
        question_count: Number(f.get("question_count")),
        seconds_per_question: Number(f.get("seconds_per_question")),
      });
      setModal(null); toast.success("تم إنشاء المسابقة"); load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "فشل إنشاء المسابقة");
    } finally { setBusy(false); }
  };

  return (
    <Shell nav={nav}>
      <h1 className="text-3xl sm:text-4xl font-black text-white glow-text">{t("dashboard")}</h1>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
        <Stat label={t("schools")} value={stats.schools ?? 0} testid="stat-schools" />
        <Stat label={t("students")} value={stats.students ?? 0} testid="stat-students" />
        <Stat label={t("teachers")} value={stats.teachers ?? 0} testid="stat-teachers" />
        <Stat label={t("topics")} value={stats.topics ?? 0} testid="stat-topics" />
        <Stat label={t("competitions")} value={stats.competitions ?? 0} testid="stat-comps" />
      </div>

      <div className="flex gap-2 mt-10 flex-wrap">
        {tabs.map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`pro-btn text-sm ${tab === k ? "text-white" : "bg-white/12 text-white border border-white/20"}`}
            style={tab === k ? { background: "linear-gradient(180deg,#ffb43f,#ef8a00)", color: "#05122e" } : {}}>{l}</button>
        ))}
      </div>

      <div className="pro-card mt-4 overflow-x-auto">
        {tab === "cms" && (
          <div data-testid="cms-panel">
            <h2 className="text-xl font-bold mb-4">لوحة إدارة المحتوى</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {[["schools", t("schools"), "#1e56c8"], ["users", t("users"), "#5a3ed6"],
                ["topics", t("topicsMgmt"), "#128577"], ["videos", t("videos"), "#c96a00"],
                ["comps", t("competitions"), "#c22239"]].map(([k, label, color]) => (
                <button key={k} data-testid={`cms-card-${k}`} onClick={() => setTab(k)}
                  className="rounded-2xl p-4 text-start text-white"
                  style={{ background: color, boxShadow: "0 8px 0 rgba(4,12,32,.3)" }}>
                  <div className="text-3xl font-black">
                    {cms?.sections?.[k === "comps" ? "competitions" : k] ?? 0}</div>
                  <div className="font-bold text-sm mt-1">{label}</div>
                  <div className="text-xs opacity-80 mt-2">تحرير →</div>
                </button>
              ))}
            </div>

            <h3 className="text-lg font-bold mt-8 mb-3">المناهج حسب الصف</h3>
            <table className="pro" data-testid="cms-grades-table">
              <thead><tr><th>{t("grade")}</th><th>{t("referenceBook")}</th><th>عدد الدروس</th>
                <th>{t("words")}</th><th>دروس فيها فيديو</th><th /></tr></thead>
              <tbody>
                {(cms?.grades || []).map((g) => (
                  <tr key={g.grade} data-testid={`cms-grade-${g.grade}`}>
                    <td className="font-bold">{g.grade}</td>
                    <td>{g.book}</td><td>{g.lessons}</td><td>{g.words}</td>
                    <td>{g.with_video}/{g.lessons}</td>
                    <td><button data-testid={`cms-edit-grade-${g.grade}`}
                      onClick={() => { setGradeFilter(String(g.grade)); setTab("topics"); }}
                      className="pro-btn bg-white/12 text-white border border-white/20 text-xs">{t("edit")}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "leads" && (
          <>
            <div className="glass-soft p-4 mb-5 flex flex-wrap items-center gap-3" data-testid="lb-scope-setting">
              <span className="text-sm font-bold">ما يراه الطالب في لوحة الصدارة:</span>
              {[["class", "صفّه فقط"], ["grade", "صفّه + طبقته"], ["school", "كل المدرسة"]].map(([k, l]) => (
                <button key={k} data-testid={`lb-scope-${k}`}
                  onClick={async () => {
                    await api.put("/settings/leaderboard", { scope: k });
                    setLbScope(k);
                    toast.success("تم تحديث نطاق لوحة الصدارة");
                  }}
                  className="pro-btn text-xs"
                  style={lbScope === k
                    ? { background: "linear-gradient(180deg,#ffb43f,#ef8a00)", color: "#05122e" }
                    : { background: "rgba(255,255,255,.12)", color: "#fff", border: "1px solid rgba(255,255,255,.2)" }}>
                  {l}</button>
              ))}
            </div>
            <h2 className="text-xl font-bold mb-4">طلبات التجربة من المدارس</h2>
            <table className="pro" data-testid="leads-table">
              <thead><tr><th>المدرسة</th><th>المسؤول</th><th>البريد</th><th>الجوال</th>
                <th>المدينة</th><th>الطلاب</th><th>إشعار البريد</th><th>الحالة</th><th /></tr></thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`}>
                    <td className="font-bold">{l.school_name}</td><td>{l.contact_name}</td>
                    <td dir="ltr">{l.email}</td><td dir="ltr">{l.phone}</td>
                    <td>{l.city || "-"}</td><td>{l.students_count || "-"}</td>
                    <td>
                      {l.notified === false ? (
                        <button data-testid={`resend-lead-${l.id}`} title={l.notify_error || ""}
                          onClick={async () => {
                            await api.post(`/leads/${l.id}/notify`);
                            toast.success("جارٍ إعادة إرسال الإشعار");
                          }}
                          className="pro-btn bg-white/12 text-white border border-white/20 text-xs">
                          إشعار البريد لم يُرسل — إعادة المحاولة</button>
                      ) : (
                        <span className="text-xs text-emerald-300">تم إشعارك بالبريد</span>
                      )}
                    </td>
                    <td>
                      <select className="input text-xs" value={l.status} data-testid={`lead-status-${l.id}`}
                        onChange={(e) => setLeadStatus(l.id, e.target.value)}>
                        <option value="new">جديد</option>
                        <option value="contacted">تم التواصل</option>
                        <option value="activated">مُفعّل</option>
                        <option value="rejected">مرفوض</option>
                      </select>
                    </td>
                    <td><button data-testid={`delete-lead-${l.id}`} className="text-red-500"
                      onClick={() => deleteLead(l.id)}><Trash2 size={16} /></button></td>
                  </tr>
                ))}
                {!leads.length && <tr><td colSpan="9" className="text-white/45">{t("noData")}</td></tr>}
              </tbody>
            </table>
          </>
        )}

        {tab === "schools" && (
          <>
            <div className="glass-soft p-4 mb-5 space-y-3" data-testid="ai-budget-panel">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-black">💰 ميزانية الذكاء الاصطناعي الشهرية</span>
                <span className="text-xs text-white/60" data-testid="ai-spend">
                  المستهلك هذا الشهر: <b style={{ color: "#ffb43f" }}>${budget.spend?.usd ?? 0}</b>
                  {" من "}${budget.monthly_usd ?? 0}
                  {" · "}{budget.spend?.total_calls ?? 0} استدعاء
                </span>
              </div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{
                  width: `${Math.min(100, ((budget.spend?.usd || 0) / (budget.monthly_usd || 1)) * 100)}%`,
                  background: "linear-gradient(90deg,#3ec46d,#ffb43f,#ff5d5d)",
                }} />
              </div>
              <div className="flex flex-wrap items-end gap-4">
                {[["monthly_usd", "الميزانية الشهرية ($)"], ["alert_pct", "أرسل تنبيهاً عند (%)"]].map(([k, label]) => (
                  <label key={k} className="block">
                    <span className="text-xs text-white/60">{label}</span>
                    <input type="number" min="1" defaultValue={budget[k]} data-testid={`budget-${k}`}
                      onBlur={async (e) => {
                        const { data } = await api.put("/settings/ai-budget", { [k]: Number(e.target.value) });
                        setBudget(data); toast.success("تم تحديث الميزانية");
                      }}
                      className="mt-1 rounded-lg bg-white/10 border border-white/20 text-white
                        text-sm text-center py-1 w-28 block" />
                  </label>
                ))}
                <label className="flex items-center gap-2 text-xs text-white/70">
                  <input type="checkbox" checked={!!budget.enabled} data-testid="budget-enabled"
                    onChange={async (e) => {
                      const { data } = await api.put("/settings/ai-budget", { enabled: e.target.checked });
                      setBudget(data);
                    }} />
                  تنبيه بالبريد الإلكتروني
                </label>
              </div>
              <p className="text-xs text-white/45">
                يُرسل تنبيه واحد فقط عند تجاوز النسبة، وتنبيه آخر عند 100% — شحن الرصيد يدوي من
                Profile ← Manage plan ← Universal Key.
              </p>
            </div>

            <div className="glass-soft p-4 mb-5 space-y-3" data-testid="ai-limits-panel">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-black">🤖 حدود الذكاء الاصطناعي اليومية لكل طالب</span>
                <span className="text-xs text-white/60" data-testid="ai-calls-today">
                  استدعاءات اليوم: <b style={{ color: "#ffb43f" }}>{ai.calls_today ?? 0}</b>
                  {" · "}طلاب نشطون: {ai.active_students_today ?? 0}
                </span>
              </div>
              <div className="flex flex-wrap items-end gap-4">
                {[["chat_per_day", "محادثات/يوم"],
                  ["quiz_per_day", "كويزات/يوم"],
                  ["writing_per_day", "تصحيح كتابة/يوم"]].map(([k, label]) => (
                  <label key={k} className="block">
                    <span className="text-xs text-white/60">{label}</span>
                    <input type="number" min="0" defaultValue={ai[k]} data-testid={`ai-${k}`}
                      onBlur={async (e) => {
                        await api.put("/settings/ai-limits", { [k]: Number(e.target.value) });
                        toast.success("تم تحديث الحد اليومي");
                        api.get("/settings/ai-limits").then((r) => setAi(r.data));
                      }}
                      className="mt-1 rounded-lg bg-white/10 border border-white/20 text-white
                        text-sm text-center py-1 w-24 block" />
                  </label>
                ))}
                <span className="text-xs text-white/45">0 = بلا حدود</span>
              </div>
            </div>
            <div className="glass-soft p-4 mb-5 space-y-3" data-testid="video-usage-panel">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-black">🎬 استهلاك الفيديو الناطق هذا الشهر:</span>
                <span className="font-black" style={{ color: "#ffb43f" }}
                  data-testid="video-used-minutes">{video.used_minutes ?? 0} دقيقة</span>
                <span className="text-xs text-white/60">
                  من {video.monthly_minutes_cap ?? 200} · المتبقي {video.left_minutes ?? 0} دقيقة</span>
                {video.paused && <span className="text-xs font-black text-red-300"
                  data-testid="video-paused-badge">⛔ متوقف تلقائياً (بلغت السقف)</span>}
              </div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full" data-testid="video-usage-bar"
                  style={{
                    width: `${Math.min(100, ((video.used_minutes || 0) / Math.max(1, video.monthly_minutes_cap || 200)) * 100)}%`,
                    background: video.paused ? "#e8384f" : "linear-gradient(90deg,#3fa9ff,#ffb43f)",
                  }} />
              </div>
              <div className="flex flex-wrap items-end gap-4">
                {[["monthly_minutes_cap", "السقف الشهري (دقائق)"],
                  ["replies_per_student_week", "ردود فيديو لكل طالب أسبوعياً"],
                  ["seconds_per_reply", "ثواني لكل رد"]].map(([k, label]) => (
                  <label key={k} className="block">
                    <span className="text-xs text-white/60">{label}</span>
                    <input type="number" min="0" defaultValue={video[k]} data-testid={`video-${k}`}
                      onBlur={async (e) => {
                        await api.put("/settings/video", { [k]: Number(e.target.value) });
                        toast.success("تم تحديث الحد");
                        api.get("/settings/video").then((r) => setVideo(r.data));
                      }}
                      className="mt-1 rounded-lg bg-white/10 border border-white/20 text-white
                        text-sm text-center py-1 w-24 block" />
                  </label>
                ))}
                <span className="text-xs text-white/50">
                  {video.provider_configured ? "المفتاح مُخزّن ✓" : "لم يُخزّن مفتاح المزوّد"}</span>
              </div>
            </div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{t("schools")}</h2>
              <button data-testid="add-school-btn" onClick={() => setModal("school")}
                className="pro-btn text-white flex items-center gap-1" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}>
                <Plus size={16} />{t("addSchool")}</button>
            </div>
            <table className="pro" data-testid="schools-table">
              <thead><tr><th>{t("name")}</th><th>{t("city")}</th><th>{t("students")}</th><th>{t("teachers")}</th><th>{t("classes")}</th><th>فيديو ناطق (مدفوع)</th><th /></tr></thead>
              <tbody>
                {schools.map((s) => (
                  <tr key={s.id} data-testid={`school-row-${s.id}`}>
                    <td className="font-bold">{s.name}</td><td>{s.city}</td>
                    <td>{s.students}</td><td>{s.teachers}</td><td>{s.classes}</td>
                    <td>
                      <div className="flex items-center gap-2">
                      <button data-testid={`toggle-video-${s.id}`}
                        onClick={async () => {
                          await api.put(`/schools/${s.id}/features`, {
                            video_chat_enabled: !s.video_chat_enabled,
                            video_quota_per_week: s.video_quota_per_week || 3,
                          });
                          toast.success(!s.video_chat_enabled ? "تم تفعيل الفيديو الناطق للمدرسة"
                            : "تم إيقاف الفيديو الناطق للمدرسة");
                          load();
                        }}
                        className="pro-btn text-xs"
                        style={s.video_chat_enabled
                          ? { background: "linear-gradient(180deg,#3ec46d,#1c8f4a)", color: "#fff" }
                          : { background: "rgba(255,255,255,.12)", color: "#fff", border: "1px solid rgba(255,255,255,.2)" }}>
                        {s.video_chat_enabled ? "مُفعّل ✓" : "مُغلق"}</button>
                      {s.video_chat_enabled && (
                        <input type="number" min="0" max="50" data-testid={`video-quota-${s.id}`}
                          defaultValue={s.video_quota_per_week || 3} title="مقاطع لكل طالب أسبوعياً"
                          onBlur={async (e) => {
                            await api.put(`/schools/${s.id}/features`, {
                              video_chat_enabled: true, video_quota_per_week: Number(e.target.value),
                            });
                            toast.success("تم تحديث السقف الأسبوعي");
                            load();
                          }}
                          className="rounded-lg bg-white/10 border border-white/20 text-white
                            text-xs text-center py-1 px-1 w-14" />
                      )}
                      </div>
                    </td>
                    <td><button data-testid={`delete-school-${s.id}`} className="text-red-500"
                      onClick={async () => { await api.delete(`/schools/${s.id}`); toast.success("deleted"); load(); }}>
                      <Trash2 size={16} /></button></td>
                  </tr>
                ))}
                {!schools.length && <tr><td colSpan="7" className="text-white/45">{t("noData")}</td></tr>}
              </tbody>
            </table>
          </>
        )}

        {tab === "users" && (
          <>
            <div className="flex justify-between items-center mb-4 gap-2 flex-wrap">
              <h2 className="text-xl font-bold">{t("users")}</h2>
              <div className="flex gap-2">
                <button data-testid="import-csv-btn" onClick={() => setModal("import")}
                  className="pro-btn bg-white/12 text-white border border-white/20 flex items-center gap-1">
                  <Upload size={16} />{t("bulkImport")}</button>
                <button data-testid="add-user-btn" onClick={() => setModal("user")}
                  className="pro-btn text-white flex items-center gap-1" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}>
                  <Plus size={16} />{t("addUser")}</button>
              </div>
            </div>
            <table className="pro" data-testid="users-table">
              <thead><tr><th>{t("fullName")}</th><th>{t("username")}</th><th>{t("role")}</th><th>{t("grade")}</th><th>{t("school")}</th><th /></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.username}`}>
                    <td className="font-bold">{u.full_name}</td><td>{u.username}</td>
                    <td>{t(u.role)}</td><td>{u.grade || "-"}</td>
                    <td>{schools.find((s) => s.id === u.school_id)?.name || "-"}</td>
                    <td className="flex gap-2">
                      <button data-testid={`preview-as-${u.username}`} title="معاينة كهذا المستخدم"
                        onClick={() => previewAs(u)}
                        className="pro-btn bg-white/12 text-white border border-white/20 text-xs flex items-center gap-1">
                        <Eye size={14} />معاينة</button>
                      <button data-testid={`edit-user-${u.username}`}
                        onClick={() => { setEditUser(u); setModal("edit-user"); }}
                        className="pro-btn bg-white/12 text-white border border-white/20 text-xs flex items-center gap-1">
                        <Pencil size={14} />تعديل</button>
                      <button className="text-red-500" data-testid={`delete-user-${u.username}`}
                        onClick={async () => { await api.delete(`/users/${u.id}`); toast.success("deleted"); load(); }}>
                        <Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {tab === "topics" && (
          <>
            <div className="flex justify-between items-center mb-4 gap-2 flex-wrap">
              <h2 className="text-xl font-bold">{t("topicsMgmt")}</h2>
              <div className="flex gap-2 items-center">
                <select className="input max-w-[160px]" value={gradeFilter} data-testid="topics-grade-filter"
                  onChange={(e) => setGradeFilter(e.target.value)}>
                  <option value="">كل الصفوف</option>
                  {GRADES.map((g) => <option key={g} value={g}>{`${t("grade")} ${g}`}</option>)}
                </select>
                <button data-testid="add-topic-btn" onClick={() => setModal("topic")}
                  className="pro-btn text-white flex items-center gap-1" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}>
                  <Plus size={16} />{t("topics")}</button>
                <button data-testid="bulk-reading-btn" disabled={!gradeFilter || busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      const { data } = await api.post("/ai/reading/bulk", { grade: Number(gradeFilter) });
                      toast.success(`جارٍ توليد نصوص القراءة لـ ${data.pending} درساً — تحديث الصفحة بعد قليل`);
                    } catch { toast.error("فشل بدء التوليد"); } finally { setBusy(false); }
                  }}
                  className="pro-btn text-[#05122e] flex items-center gap-1 disabled:opacity-50"
                  style={{ background: "linear-gradient(180deg,#ffb43f,#ef8a00)" }}>
                  <Wand2 size={16} />توليد نصوص القراءة للصف</button>
              </div>
            </div>
            <table className="pro" data-testid="topics-table">
              <thead><tr><th>{t("grade")}</th><th>{t("title")}</th><th>{t("words")}</th><th>{t("video")}</th><th>النص</th><th /><th /></tr></thead>
              <tbody>
                {topics.filter((tp) => !gradeFilter || String(tp.grade) === gradeFilter)
                  .sort((a, b) => a.grade - b.grade || a.title_en.localeCompare(b.title_en)).map((tp) => (
                  <tr key={tp.id} data-testid={`topic-row-${tp.id}`}>
                    <td className="font-bold">{tp.grade}</td>
                    <td>{tp.title_en}<div className="text-xs text-white/45">{tp.title_ar}</div></td>
                    <td>{(tp.words || []).length}</td>
                    <td className="text-white/60 truncate max-w-[160px]">
                      {tp.video_id
                        ? (videos.find((v) => v.id === tp.video_id)?.original_filename || "ملف مرفوع")
                        : (tp.video_url || "-")}</td>
                    <td>{tp.reading_text
                      ? <span className="text-xs text-emerald-300" data-testid={`reading-ok-${tp.id}`}>✓ يوجد نص</span>
                      : <span className="text-xs text-white/45">—</span>}</td>
                    <td><button data-testid={`edit-topic-${tp.id}`} onClick={() => { setEditTab("fields"); setEditing(tp); }}
                      className="pro-btn bg-white/12 text-white border border-white/20 text-xs">{t("edit")}</button></td>
                    <td><button className="text-red-500" data-testid={`delete-topic-${tp.id}`}
                      onClick={async () => { await api.delete(`/topics/${tp.id}`); toast.success("deleted"); load(); }}>
                      <Trash2 size={16} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {tab === "videos" && (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2"><Film size={18} />{t("videos")}</h2>
              <button data-testid="upload-video-btn" onClick={() => setModal("video")}
                className="pro-btn text-white flex items-center gap-1" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}>
                <Upload size={16} />{t("uploadVideo")}</button>
            </div>
            <table className="pro" data-testid="videos-table">
              <thead><tr><th>{t("name")}</th><th>Size</th><th>{t("topics")}</th><th /></tr></thead>
              <tbody>
                {videos.map((v) => (
                  <tr key={v.id} data-testid={`video-row-${v.id}`}>
                    <td className="font-bold">{v.original_filename}</td>
                    <td>{(v.size / 1048576) >= 1 ? `${(v.size / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(v.size / 1024))} KB`}</td>
                    <td>
                      <select className="input max-w-[240px]" data-testid={`attach-video-${v.id}`}
                        value={topics.find((x) => x.video_id === v.id)?.id || ""}
                        onChange={(e) => {
                          const prev = topics.find((x) => x.video_id === v.id);
                          const next = topics.find((x) => x.id === e.target.value);
                          if (prev && prev.id !== e.target.value) attachVideo(prev, null);
                          if (next) attachVideo(next, v.id);
                          else if (prev) toast.success("تم فصل الفيديو");
                        }}>
                        <option value="">— اربط بموضوع —</option>
                        {[...topics].sort((a, b) => a.grade - b.grade).map((tp) => (
                          <option key={tp.id} value={tp.id}>
                            {`${t("grade")} ${tp.grade} · ${tp.title_en}`}</option>
                        ))}
                      </select>
                    </td>
                    <td><button className="text-red-500" data-testid={`delete-video-${v.id}`}
                      onClick={async () => { await api.delete(`/videos/${v.id}`); toast.success("deleted"); load(); }}>
                      <Trash2 size={16} /></button></td>
                  </tr>
                ))}
                {!videos.length && <tr><td colSpan="4" className="text-white/45">{t("noData")}</td></tr>}
              </tbody>
            </table>
          </>
        )}

        {tab === "comps" && (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2"><Trophy size={18} />{t("competitions")}</h2>
              <button data-testid="add-comp-btn" onClick={() => setModal("comp")}
                className="pro-btn text-white flex items-center gap-1" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}>
                <Plus size={16} />{t("create")}</button>
            </div>
            <table className="pro" data-testid="comps-table">
              <thead><tr><th>{t("title")}</th><th>{t("grade")}</th><th>Topic</th><th>{t("questions")}</th><th>Start</th><th>End</th><th /></tr></thead>
              <tbody>
                {comps.map((c) => (
                  <tr key={c.id} data-testid={`comp-row-${c.id}`}>
                    <td className="font-bold">{c.title}</td><td>{c.grade}</td>
                    <td>{c.topic}</td><td>{c.question_count} · {c.seconds_per_question}s</td>
                    <td>{c.starts_at ? new Date(c.starts_at).toLocaleString() : "-"}</td>
                    <td>{c.ends_at ? new Date(c.ends_at).toLocaleString() : "-"}</td>
                    <td><button className="text-red-500" data-testid={`delete-comp-${c.id}`}
                      onClick={async () => { await api.delete(`/competitions/${c.id}`); toast.success("deleted"); load(); }}>
                      <Trash2 size={16} /></button></td>
                  </tr>
                ))}
                {!comps.length && <tr><td colSpan="7" className="text-white/45">{t("noData")}</td></tr>}
              </tbody>
            </table>
          </>
        )}
      </div>

      <Modal open={modal === "school"} onClose={() => setModal(null)} title={t("addSchool")} testid="school-modal">
        <form onSubmit={addSchool} className="space-y-3">
          <input name="name" className="input" placeholder={t("name")} required data-testid="school-name-input" />
          <input name="city" className="input" placeholder={t("city")} />
          <button className="pro-btn text-white w-full" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}
            data-testid="school-save-btn">{t("save")}</button>
        </form>
      </Modal>

      <Modal open={modal === "user"} onClose={() => setModal(null)} title={t("addUser")} testid="user-modal">
        <form onSubmit={addUser} className="space-y-3">
          <input name="full_name" className="input" placeholder={t("fullName")} required data-testid="user-name-input" />
          <input name="username" className="input" placeholder={t("username")} required data-testid="user-username-input" />
          <input name="password" className="input" placeholder={t("password")} required data-testid="user-password-input" />
          <select name="role" className="input" data-testid="user-role-select">
            <option value="student">{t("student")}</option>
            <option value="teacher">{t("teacher")}</option>
            <option value="school_admin">{t("school_admin")}</option>
          </select>
          <select name="school_id" className="input" required data-testid="user-school-select">
            {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select name="grade" className="input" data-testid="user-grade-select">
            <option value="">{t("grade")} —</option>
            {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <button className="pro-btn text-white w-full" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}
            data-testid="user-save-btn">{t("save")}</button>
        </form>
      </Modal>

      <Modal open={modal === "edit-user"} onClose={() => setModal(null)}
        title={`تعديل الحساب — ${editUser?.full_name || ""}`} testid="edit-user-modal">
        <form onSubmit={saveUserEdit} className="space-y-3" key={editUser?.id}>
          <input name="full_name" className="input" defaultValue={editUser?.full_name}
            placeholder={t("fullName")} data-testid="edit-user-name" />
          <input name="username" className="input" defaultValue={editUser?.username} dir="ltr"
            placeholder={t("username")} data-testid="edit-user-username" />
          <input name="password" className="input" dir="ltr"
            placeholder="كلمة سر جديدة (اتركها فارغة لعدم التغيير)" data-testid="edit-user-password" />
          <select name="role" className="input" defaultValue={editUser?.role} data-testid="edit-user-role">
            <option value="student">{t("student")}</option>
            <option value="teacher">{t("teacher")}</option>
            <option value="school_admin">{t("school_admin")}</option>
          </select>
          <select name="school_id" className="input" defaultValue={editUser?.school_id || ""}
            data-testid="edit-user-school">
            <option value="">— بدون مدرسة —</option>
            {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select name="grade" className="input" defaultValue={editUser?.grade || ""}
            data-testid="edit-user-grade">
            <option value="">{t("grade")} —</option>
            {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <button className="pro-btn text-white w-full" data-testid="edit-user-save"
            style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}>{t("save")}</button>
        </form>
      </Modal>

      <Modal open={modal === "import"} onClose={() => setModal(null)} title={t("bulkImport")} testid="import-modal">
        <form onSubmit={importCsv} className="space-y-3">
          <p className="text-sm text-white/70">CSV columns: username, password, full_name, role, grade</p>
          <select name="school_id" className="input" required data-testid="import-school-select">
            {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input name="file" type="file" accept=".csv" className="input" required data-testid="import-file-input" />
          <button disabled={busy} className="pro-btn text-white w-full" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}
            data-testid="import-submit-btn">{busy ? t("loading") : t("bulkImport")}</button>
        </form>
      </Modal>

      <Modal open={modal === "topic"} onClose={() => setModal(null)} title={t("topicsMgmt")} testid="topic-modal">
        <form onSubmit={addTopic} className="space-y-3">
          <select name="grade" className="input" data-testid="topic-grade-select">
            {GRADES.map((g) => <option key={g} value={g}>{`${t("grade")} ${g} — ${BOOKS[g]}`}</option>)}
          </select>
          <input name="title_en" className="input" placeholder="Title (English)" required data-testid="topic-title-en" />
          <input name="title_ar" className="input" placeholder="العنوان بالعربي" required data-testid="topic-title-ar" />
          <button type="button" disabled={busy} data-testid="topic-ai-btn"
            onClick={(e) => aiFill(e.target.form)}
            className="pro-btn w-full border border-slate-200 flex items-center justify-center gap-2">
            <Wand2 size={16} />{busy ? t("loading") : "توليد المحتوى بالذكاء الاصطناعي"}</button>
          <textarea name="summary_ar" className="input" rows="2" placeholder="ملخص بالعربي" data-testid="topic-summary" />
          <textarea name="content_en" className="input" rows="4" placeholder="Lesson content (English)" data-testid="topic-content" />
          <input name="words" className="input" placeholder="words, comma, separated" data-testid="topic-words" />
          <input name="video_url" className="input" placeholder="YouTube / video URL" data-testid="topic-video" />
          <select name="video_id" className="input" data-testid="topic-video-select">
            <option value="">— {t("uploadedVideo")} —</option>
            {videos.map((v) => <option key={v.id} value={v.id}>{v.original_filename}</option>)}
          </select>
          <button className="pro-btn text-white w-full" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}
            data-testid="topic-save-btn">{t("save")}</button>
        </form>
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)}
        title={`${t("edit")} — ${editing?.title_en || ""}`} testid="edit-topic-modal">
        {editing && (
          <>
          <div className="flex gap-2 mb-4">
            {[["fields", "الحقول"], ["reading", "نص القراءة"], ["layout", "شكل الصفحة"]].map(([k, l]) => (
              <button key={k} type="button" data-testid={`edit-tab-${k}`} onClick={() => setEditTab(k)}
                className={`pro-btn text-xs ${editTab === k ? "text-[#05122e]" : "bg-white/12 text-white border border-white/20"}`}
                style={editTab === k ? { background: "linear-gradient(180deg,#ffb43f,#ef8a00)" } : {}}>{l}</button>
            ))}
          </div>
          {editTab === "layout" ? (
            <BlocksEditor topic={editing} videos={videos}
              onSave={() => { setEditing(null); load(); }} />
          ) : editTab === "reading" ? (
            <div className="space-y-3" data-testid="reading-editor">
              <button type="button" disabled={busy} data-testid="reading-generate-btn"
                onClick={async () => {
                  setBusy(true);
                  try {
                    const { data } = await api.post(`/ai/reading/${editing.id}`);
                    setEditing({ ...editing, ...data });
                    toast.success("تم توليد نص أصلي جديد");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "فشل التوليد");
                  } finally { setBusy(false); }
                }}
                className="pro-btn text-[#05122e] w-full flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: "linear-gradient(180deg,#ffb43f,#ef8a00)" }}>
                <Wand2 size={16} />{busy ? t("loading") : "توليد نص قراءة أصلي (بعنوان جديد)"}</button>
              <label className="block">
                <span className="text-xs font-bold text-white/60">عنوان النص (إنجليزي)</span>
                <input className="input mt-1" dir="ltr" value={editing.reading_title || ""}
                  data-testid="reading-title-input"
                  onChange={(e) => setEditing({ ...editing, reading_title: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-xs font-bold text-white/60">عنوان النص (عربي)</span>
                <input className="input mt-1" value={editing.reading_title_ar || ""}
                  data-testid="reading-title-ar-input"
                  onChange={(e) => setEditing({ ...editing, reading_title_ar: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-xs font-bold text-white/60">النص</span>
                <textarea className="input mt-1" dir="ltr" rows={10} value={editing.reading_text || ""}
                  data-testid="reading-text-input"
                  onChange={(e) => setEditing({ ...editing, reading_text: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-xs font-bold text-white/60">شرح مختصر بالعربية</span>
                <textarea className="input mt-1" rows={2} value={editing.reading_summary_ar || ""}
                  data-testid="reading-summary-input"
                  onChange={(e) => setEditing({ ...editing, reading_summary_ar: e.target.value })} />
              </label>
              <div className="text-xs text-white/60" data-testid="reading-questions-count">
                أسئلة الفهم: {(editing.reading_questions || []).length}
                {(editing.reading_questions || []).length === 0 && " — استخدم زر التوليد لإنشائها"}
              </div>
              <button type="button" disabled={busy} data-testid="reading-save-btn"
                onClick={async () => {
                  setBusy(true);
                  try {
                    await api.put(`/topics/${editing.id}`, editing);
                    toast.success("تم حفظ النص");
                    setEditing(null); load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "فشل الحفظ");
                  } finally { setBusy(false); }
                }}
                className="pro-btn text-white w-full" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}>
                {t("save")}</button>
            </div>
          ) : (
          <form onSubmit={saveTopic} className="space-y-3">
            <select name="grade" className="input" defaultValue={editing.grade} data-testid="edit-grade-select">
              {GRADES.map((g) => <option key={g} value={g}>{`${t("grade")} ${g}`}</option>)}
            </select>
            <input name="title_en" className="input" defaultValue={editing.title_en} required data-testid="edit-title-en" />
            <input name="title_ar" className="input" defaultValue={editing.title_ar} required data-testid="edit-title-ar" />
            <textarea name="summary_ar" rows="2" className="input" defaultValue={editing.summary_ar} data-testid="edit-summary" />
            <textarea name="content_en" rows="4" className="input" defaultValue={editing.content_en} data-testid="edit-content" />
            <input name="words" className="input" defaultValue={(editing.words || []).join(", ")} data-testid="edit-words" />
            <input name="video_url" className="input" defaultValue={editing.video_url || ""}
              placeholder="YouTube / video URL" data-testid="edit-video-url" />
            <select name="video_id" className="input" defaultValue={editing.video_id || ""} data-testid="edit-video-select">
              <option value="">— {t("uploadedVideo")} —</option>
              {videos.map((v) => <option key={v.id} value={v.id}>{v.original_filename}</option>)}
            </select>
            <button className="pro-btn text-white w-full" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}
              data-testid="edit-topic-save">{t("save")}</button>
          </form>
          )}
          </>
        )}
      </Modal>

      <Modal open={modal === "video"} onClose={() => setModal(null)} title={t("uploadVideo")} testid="video-modal">
        <form onSubmit={uploadVideo} className="space-y-3">
          <p className="text-sm text-white/70">mp4 / webm / mov — الحد الأقصى 220 ميجابايت</p>
          <input name="file" type="file" accept="video/*" className="input" required data-testid="video-file-input" />
          <button disabled={busy} className="pro-btn text-white w-full" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}
            data-testid="video-upload-submit">{busy ? "جارٍ الرفع..." : t("uploadVideo")}</button>
        </form>
      </Modal>

      <Modal open={modal === "comp"} onClose={() => setModal(null)} title={t("competitions")} testid="comp-modal">
        <form onSubmit={addComp} className="space-y-3">
          <input name="title" className="input" placeholder={t("title")} required data-testid="comp-title-input" />
          <select name="grade" className="input" data-testid="comp-grade-select">
            {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <input name="topic" className="input" placeholder="Topic" defaultValue="General English" />
          <div className="grid grid-cols-2 gap-3">
            <select name="question_count" className="input" data-testid="comp-count-select" defaultValue="5">
              {[3, 5, 8, 10].map((n) => <option key={n} value={n}>{n} {t("questions")}</option>)}
            </select>
            <select name="seconds_per_question" className="input" data-testid="comp-seconds-select" defaultValue="20">
              {[10, 15, 20, 30].map((n) => <option key={n} value={n}>{n}s / سؤال</option>)}
            </select>
          </div>
          <input name="starts_at" type="datetime-local" className="input" data-testid="comp-start-input" />
          <input name="ends_at" type="datetime-local" className="input" data-testid="comp-end-input" />
          <button disabled={busy} className="pro-btn text-white w-full" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}
            data-testid="comp-save-btn">{t("save")} (AI يولّد الأسئلة)</button>
        </form>
      </Modal>
    </Shell>
  );
}
